export type FormMetadata = {
  form_id: string;
  form_name: string;
  created_by: string;
  created_at: string;
  updated_by: string;
  updated_at: string;
  languages?: string[];
};

/** Visible table columns only; languages live in a separate lookup index. */
export type FormTableRow = Omit<FormMetadata, 'languages'>;

export type FormTemplate = {
  form_id: string;
  form_name: string;
  show_form_after_fill: boolean;
  questions: FormItem[];
};

/** Question type value for page-break items (structural only, not answerable). */
export const PAGE_BREAK_QUESTION_TYPE = 'page_break';

export const QUESTION_TYPE_MAP_TRANSLATION: Record<string, string> = {
  header: 'כותרת / תמונה',
  select: 'רשימה נפתחת',
  text: 'שאלה פתוחה',
  radio: 'כפתורי רדיו',
  checkbox: 'תיבת סימון',
  scale: 'סולם',
  [PAGE_BREAK_QUESTION_TYPE]: 'מעבר דף',
};

export const FIELD_TYPE_MAP_TRANSLATION: Record<string, string> = {
  datetime: 'תאריך',
  number: 'מספר',
  text: 'טקסט',
};

/** Display-only label for number questions; keep short to avoid layout/XSS concerns. */
export const MEASUREMENT_UNIT_LABEL = 'יחידת מידה';
export const MEASUREMENT_UNIT_PLACEHOLDER = 'לדוגמה: מ"ל, kg';
export const MEASUREMENT_UNIT_MAX_LENGTH = 30;

/** Hebrew labels for translation slot names (server missing_translations and the modal). */
export const MISSING_SLOT_LABELS: Record<string, string> = {
  text: 'טקסט השאלה',
  description: 'הסבר',
  option: 'אפשרות',
  scale_min_text: 'טקסט תחתית הסולם',
  scale_max_text: 'טקסט ראש הסולם',
  text_before: 'טקסט לפני',
  text_after: 'טקסט אחרי',
  measurement_unit: MEASUREMENT_UNIT_LABEL,
};

export const OPERATOR_OPTIONS = [
  { value: 'greater_than', label: 'גדול מ' },
  { value: 'greater_equal', label: 'גדול שווה' },
  { value: 'less_than', label: 'קטן מ' },
  { value: 'less_equal', label: 'קטן שווה' },
  { value: 'contains', label: 'מכיל אחד מהערכים' },
  { value: 'not_contains', label: 'לא מכיל אחד הערכים' },
];

export const CONNECTOR_OPTIONS = [
  { value: 'first', label: 'ראשון' },
  { value: 'and', label: 'וגם' },
  { value: 'or', label: 'או' },
];

export type Condition = {
  values: string[];
  operator: string;
  connector: string;
  connected_question_guid: string;
};

export type Option = {
  guid: string;
  text: string | number;
  exclusive?: boolean;
  active?: boolean;
};

// Preset colors for child question templates (24 colors)
export const TEMPLATE_BADGE_COLORS = [
  { value: '#20c997', label: 'טורקיז' }, // Teal (default)
  { value: '#0d6efd', label: 'כחול' }, // Blue
  { value: '#6f42c1', label: 'סגול' }, // Purple
  { value: '#d63384', label: 'ורוד' }, // Pink
  { value: '#dc3545', label: 'אדום' }, // Red
  { value: '#fd7e14', label: 'כתום' }, // Orange
  { value: '#ffc107', label: 'צהוב' }, // Yellow
  { value: '#198754', label: 'ירוק' }, // Green
  { value: '#0dcaf0', label: 'תכלת' }, // Cyan
  { value: '#6610f2', label: 'אינדיגו' }, // Indigo
  { value: '#adb5bd', label: 'אפור' }, // Gray
  { value: '#212529', label: 'שחור' }, // Black
  { value: '#17a2b8', label: 'טורקיז כהה' }, // Dark Teal
  { value: '#28a745', label: 'ירוק בהיר' }, // Light Green
  { value: '#e83e8c', label: "מג'נטה" }, // Magenta
  { value: '#6c757d', label: 'אפור כהה' }, // Dark Gray
  { value: '#84cc16', label: 'ליים' }, // Lime
  { value: '#343a40', label: 'פחם' }, // Charcoal
  { value: '#f9a8d4', label: 'ורוד בייבי' }, // Baby Pink
  { value: '#c4b5fd', label: 'לילך' }, // Lilac
  { value: '#92400e', label: 'חום' }, // Brown
  { value: '#6c3483', label: 'סגול כהה' }, // Dark Purple
  { value: '#1abc9c', label: 'ים' }, // Sea
  { value: '#e74c3c', label: 'קורל' }, // Coral
];

export const DEFAULT_BADGE_COLOR = '#20c997'; // Teal

export type ChildQuestionTemplate = {
  id: string; // Unique ID for this template
  option_guids: string[]; // Which parent options trigger this template
  text_before?: string; // Text to appear before the answer in child question
  text_after?: string; // Text to appear after the answer in child question
  question_type?: string;
  options?: Option[]; // Shared options for all child questions created by this template
  field_type?: string;
  key_prefix?: string; // User-defined prefix for child question keys (e.g., "diagnosed" -> parent_diagnosed_1)
  badge_color?: string; // Color for the badge shown on auto-generated child questions
  measurement_unit?: string; // Optional unit label for number field (e.g. "מ\"ל", "kg") - displayed next to input
  show_description?: boolean; // Same as Question: whether to show explanation (reused from question modal pattern)
  description?: string; // Optional explanation text, same for all child questions from this template
};

/** Answerable question types (excludes page_break). */
export type AnswerableQuestionType = 'header' | 'select' | 'text' | 'radio' | 'checkbox' | 'scale';

/** Structural item for splitting the form into pages; not answerable. */
export type PageBreak = {
  question_type: typeof PAGE_BREAK_QUESTION_TYPE;
  guid: string;
  question_index: number;
  show_question?: boolean;
  options: Option[];
};

/** Answerable form question with required question_key and question_type. */
export type FormQuestionItem = {
  question_index: number;
  text: string;
  guid: string;
  show_description?: boolean;
  mandatory?: boolean;
  description?: string;
  question_key: string;
  show_question: boolean;
  question_type: AnswerableQuestionType;
  options: Option[];
  question_conditions?: Condition[];
  appeared_conditions?: Condition[];
  min_value?: number;
  max_value?: number;
  field_type?: string;
  display_type?: string;
  multiple_select?: boolean;
  scale_min_text?: string;
  scale_max_text?: string;
  scale_num_options?: number;
  formula?: string;
  answer_depends_on_question?: boolean;
  dependency_question_guid?: string;
  dependency_operator?: string;
  date_comparison_enabled?: boolean;
  date_comparison_operator?: string;
  measurement_unit?: string;
  auto_create_child_questions?: boolean;
  child_question_templates?: ChildQuestionTemplate[];
  auto_generated_from_parent?: string;
  auto_generated_from_template?: string;
  auto_generated_badge_color?: string;
};

/** Per-question strings entry in a `{form_id}_form_template.strings-{lang}.json` file. */
export type StringsEntry = {
  text?: string;
  question_plain_text?: string;
  description?: string;
  scale_min_text?: string;
  scale_max_text?: string;
  measurement_unit?: string;
  options?: Record<string, { text: string }>;
  child_templates?: Record<
    string,
    {
      text_before?: string;
      text_after?: string;
      description?: string;
      measurement_unit?: string;
      options?: Record<string, { text: string }>;
    }
  >;
};

/** Guid-keyed strings file loaded from S3. */
export type StringsFile = Record<string, StringsEntry>;

/** Language-agnostic structure file (same shape as FormTemplate). */
export type StructureFile = FormTemplate;

/** Discriminated union: either an answerable question or a page break. */
export type FormItem = FormQuestionItem | PageBreak;

/** Type guard for page break items. */
export function isPageBreak(item: FormItem): item is PageBreak {
  return item.question_type === PAGE_BREAK_QUESTION_TYPE;
}

/**
 * Legacy question type for API/backward compatibility (question_key and question_type optional).
 * Prefer QuestionItem and isPageBreak() in console list editing.
 */
export type Question = {
  question_index: number;
  text: string;
  guid: string;
  show_description?: boolean;
  mandatory?: boolean;
  description?: string;
  question_key?: string;
  show_question: boolean;
  question_type?: string;
  options: Option[];
  question_conditions?: Condition[];
  appeared_conditions?: Condition[];
  min_value?: number;
  max_value?: number;
  field_type?: string;
  display_type?: string;
  multiple_select?: boolean;
  scale_min_text?: string;
  scale_max_text?: string;
  scale_num_options?: number;
  formula?: string;
  answer_depends_on_question?: boolean;
  dependency_question_guid?: string;
  dependency_operator?: string;
  date_comparison_enabled?: boolean;
  date_comparison_operator?: string;
  measurement_unit?: string;
  auto_create_child_questions?: boolean;
  child_question_templates?: ChildQuestionTemplate[];
  auto_generated_from_parent?: string;
  auto_generated_from_template?: string;
  auto_generated_badge_color?: string;
};
